import './common/types/pwa-register.d.ts';
import './common/types/global.d.ts';
import 'clipsal-cortex-utils/src/build/polyfills';
import 'react-app-polyfill/stable';
import 'focus-visible/dist/focus-visible'; // Removes blue border when clicking focusable items
import './index.css';
import './i18n';

import React from 'react';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { createRoutes } from './common/components/route/createRoutes';
import { IS_RUNNING_TESTS } from './common/constants';
import { AppProvider } from './Provider';
import { routes } from './routes';
import { setupAPI } from './utils/common/setup-api';
import { setupSentry } from './utils/common/setup-sentry';

/**
 * Initialises the React application. Handles setup for the following top-level systems:
 *
 * - Sentry (error and performance tracking)
 * - Logrocket (error and performance tracking)
 * - Google Analytics (analytics tracking)
 * - Amplify (API automation system from AWS)
 *
 * Finally, the root DOM node (<App />) is rendered.
 */
async function setupReactDOM() {
  // Only send events to sentry in staging and production environments
  if (import.meta.env['MODE'] !== 'development' && !IS_RUNNING_TESTS) setupSentry();

  setupAPI();

  const container = document.getElementById('root');
  const root = createRoot(container!);

  const reactRoutes = createRoutes(routes);
  const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(
    // Hackfix until Sentry react version is v19
    createBrowserRouter as Parameters<typeof Sentry.wrapCreateBrowserRouter>[0]
  );
  const router = sentryCreateBrowserRouter([{ element: <AppProvider />, children: reactRoutes }]);

  // eslint-disable-next-line react/jsx-no-undef
  root.render(<RouterProvider router={router as ReturnType<typeof createBrowserRouter>} />);
}

setupReactDOM();
